import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Typography } from '@mui/material';
import { supabase } from '../config/supabase';
import { useAuth } from '../auth/AuthContext';

type Assignment = { playerId: string; positionId: string | null; slot?: number | null };
type ReviewItem = {
  playerId: string;
  kind: 'did_not_play' | 'played_unexpectedly';
  plannedPositionId: string | null;
  actualPositionId: string | null;
  proposedChoice: string;
  confirmedChoice: string | null;
  existingDecision?: string | null;
  reviewedAt?: string | null;
  staleAfterCorrection?: boolean;
};
type Review = {
  matchId: string;
  status: string;
  published: Assignment[];
  actual: Assignment[];
  items: ReviewItem[];
  pendingCount: number;
};

const choices: Record<ReviewItem['kind'], { value: string; label: string }[]> = {
  did_not_play: [
    { value: 'late_cancel_count', label: 'Count as an opportunity — Late Cancel' },
    { value: 'did_not_play_no_count', label: 'Do not count — did not play' },
  ],
  played_unexpectedly: [
    { value: 'played_count', label: 'Count as Played' },
    { value: 'late_sub_no_count', label: 'Late Sub — Do Not Count Toward Fairness' },
  ],
};

export function ParticipationReconciliation({ matchId, teamId, matchDate, opponent }: { matchId: string; teamId: string; matchDate: string; opponent: string }) {
  const auth = useAuth();
  const [review, setReview] = useState<Review | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [positions, setPositions] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error: readError } = await supabase.rpc('participation_reconciliation_get', { p_match_event_id: matchId });
      if (readError) throw readError;
      const next = data as Review | null;
      setReview(next && (next.items?.length || ['failed', 'evidence_unavailable'].includes(next.status)) ? next : null);
      if (next) {
        setSelected(Object.fromEntries(next.items.map(item => [item.playerId, item.confirmedChoice || item.proposedChoice])));
        const playerIds = [...new Set([...next.published, ...next.actual].map(row => row.playerId))];
        const positionIds = [...new Set([...next.published, ...next.actual].map(row => row.positionId).filter((id): id is string => Boolean(id)))];
        const [playersResult, positionsResult] = await Promise.all([
          playerIds.length ? supabase.from('players').select('id,first_name,last_name').in('id', playerIds) : Promise.resolve({ data: [], error: null }),
          positionIds.length ? supabase.from('league_configuration_positions').select('id,position_code').in('id', positionIds) : Promise.resolve({ data: [], error: null }),
        ]);
        if (playersResult.error || positionsResult.error) throw new Error(playersResult.error?.message || positionsResult.error?.message);
        setNames(Object.fromEntries((playersResult.data || []).map(row => [row.id, [row.first_name, row.last_name].filter(Boolean).join(' ')])));
        setPositions(Object.fromEntries((positionsResult.data || []).map(row => [row.id, row.position_code])));
      }
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to load participation review.');
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!review || window.sessionStorage.getItem('courtcaptain:participation-review-target') !== matchId) return;
    window.sessionStorage.removeItem('courtcaptain:participation-review-target');
    window.requestAnimationFrame(() => document.getElementById('participation-reconciliation')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [matchId, review]);

  const pending = useMemo(() => review?.items.filter(item => !item.confirmedChoice && !item.staleAfterCorrection).length || 0, [review]);
  const assignment = (positionId: string | null) => positionId ? positions[positionId] || 'Line' : '—';
  const save = async (items: ReviewItem[]) => {
    if (!items.length) return;
    setSaving(true);
    setSuccess('');
    try {
      for (const item of items) {
        const { error: saveError } = await supabase.rpc('participation_reconciliation_save', {
          p_match_event_id: matchId,
          p_player_id: item.playerId,
          p_choice: selected[item.playerId],
        });
        if (saveError) throw saveError;
      }
      await load();
      setSuccess(items.length === 1 ? 'Decision saved.' : 'Decisions saved.');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to save participation decision.');
      await load();
    } finally {
      setSaving(false);
    }
  };

  const isAdministrator = ['admin', 'owner'].includes(auth.appUser?.role || '');
  const matchingAssignment = auth.teamAssignments.find(row => row.team_id === teamId && row.is_active && !row.deleted_at && ['captain', 'co_captain'].includes(row.assignment_role));
  const canRead = isAdministrator || matchingAssignment?.matches_lineups_access === 'edit' || matchingAssignment?.matches_lineups_access === 'view';
  const canWrite = isAdministrator || matchingAssignment?.matches_lineups_access === 'edit';
  if (!canRead) return null;
  if (loading && !review) return <Box sx={{ py: 1 }}><CircularProgress size={20} aria-label="Loading participation review" /></Box>;
  if (!review && !error) return null;

  return <Paper id="participation-reconciliation" variant="outlined" sx={{ p: 2, mt: 2 }}>
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
        <Typography variant="h6">Participation Reconciliation</Typography>
        {review && <Chip size="small" color={pending ? 'warning' : 'success'} label={`${review.status.replaceAll('_', ' ')} · ${pending} need review`} />}
      </Stack>
      <Typography variant="body2" color="text.secondary">{matchDate} · vs {opponent} · Official result applied. Fairness decisions do not change the official scorecard or who actually played.</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}
      {review && <>
        {review.status === 'failed' && <Alert severity="error">The official participation comparison could not be completed. An administrator should inspect the result application before fairness decisions are saved.</Alert>}
        {review.status === 'evidence_unavailable' && <Alert severity="warning">The original published lineup cannot be verified from the available records. No fairness decision has been inferred.</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          <Box><Typography variant="subtitle2">Published lineup</Typography>{review.published.map((row, index) => <Typography key={`${row.playerId}-${index}`} variant="body2">{assignment(row.positionId)} · {names[row.playerId] || 'Player'}</Typography>)}</Box>
          <Box><Typography variant="subtitle2">Official actual lineup</Typography>{review.actual.map((row, index) => <Typography key={`${row.playerId}-${index}`} variant="body2">{assignment(row.positionId)} · {names[row.playerId] || 'Player'}</Typography>)}</Box>
        </Box>
        <Typography variant="subtitle2">{review.items.length} affected player{review.items.length === 1 ? '' : 's'}</Typography>
        {review.items.map(item => <Paper key={item.playerId} variant="outlined" sx={{ p: 1.5 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">{names[item.playerId] || 'Player'} · {item.kind === 'did_not_play' ? 'Did not play' : 'Played unexpectedly'}</Typography>
            {item.staleAfterCorrection ? <Alert severity="info">Historical review item superseded by an audited result correction. Use the corrected actual lineup above. Its prior fairness decision and cancellation/substitute history are preserved.</Alert> : <Typography variant="body2">Published: {assignment(item.plannedPositionId)} · Actual: {assignment(item.actualPositionId)} · Officially played: {item.kind === 'played_unexpectedly' ? 'Yes' : 'No'}</Typography>}
            {item.existingDecision && <Typography variant="caption" color="text.secondary">Existing captain decision: {item.existingDecision}</Typography>}
            <Typography variant="caption" color="text.secondary">{item.confirmedChoice ? `Saved: ${choices[item.kind].find(choice => choice.value === item.confirmedChoice)?.label || item.confirmedChoice}` : `Proposed (not saved): ${choices[item.kind].find(choice => choice.value === item.proposedChoice)?.label || item.proposedChoice}`}</Typography>
            <FormControl size="small" fullWidth><InputLabel id={`fairness-${item.playerId}`}>Fairness treatment</InputLabel><Select labelId={`fairness-${item.playerId}`} label="Fairness treatment" value={selected[item.playerId] || item.proposedChoice} onChange={event => setSelected(current => ({ ...current, [item.playerId]: event.target.value }))} disabled={saving || !canWrite || item.staleAfterCorrection}>{choices[item.kind].map(choice => <MenuItem key={choice.value} value={choice.value}>{choice.label}</MenuItem>)}</Select></FormControl>
            {canWrite && <Box><Button variant="outlined" size="small" disabled={saving || item.staleAfterCorrection} onClick={() => void save([item])}>{item.confirmedChoice ? 'Update Decision' : 'Save Decision'}</Button></Box>}
          </Stack>
        </Paper>)}
        {canWrite && <Box><Button variant="contained" disabled={saving || !review.items.some(item => !item.confirmedChoice && !item.staleAfterCorrection)} onClick={() => void save(review.items.filter(item => !item.confirmedChoice && !item.staleAfterCorrection))}>Save Remaining Decisions</Button></Box>}
      </>}
    </Stack>
  </Paper>;
}
